import React, { useState } from 'react';
import axios from 'axios';

function App() {
  const [deployments, setDeployments] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDeploymentId, setSelectedDeploymentId] = useState(null);
  const [passcode, setPasscode] = useState('');

  // Fetch the list of deployments
  const fetchDeployments = async () => {
    setLoading(true);
    setError('');
    setLogs('');
    try {
      const response = await axios.get('/api/deployments', {
        headers: {
          'X-Passcode': passcode
        }
      });
      setDeployments(response.data.result || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid passcode. Please try again.');
      } else {
        setError('Error fetching deployments. Check console and config.');
      }
      console.error(err);
    }
    setLoading(false);
  };

  // Fetch logs for a specific deployment
  const fetchLogs = async (deploymentId) => {
    setLoading(true);
    setError('');
    setLogs([]);
    setSelectedDeploymentId(deploymentId);
    try {
      const response = await axios.get(`/api/deployments/${deploymentId}/logs`, {
        headers: {
          'X-Passcode': passcode
        }
      });
      const logsData = Array.isArray(response.data.result) ? response.data.result : [];
      setLogs(logsData);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Invalid passcode. Please try again.');
      } else {
        setError('Error fetching logs for this deployment.');
      }
      console.error(err);
      setLogs([]);
    }
    setLoading(false);
  };

  return (
    <div style={{ 
      fontFamily: 'sans-serif', 
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: '#f5f5f5'
    }}>
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Build Log Viewer</h1>
      
      <div className="flex gap-4 items-center mb-4">
        <input
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          placeholder="Enter passcode"
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button 
          onClick={fetchDeployments} 
          disabled={loading || !passcode}
          className={`px-4 py-2 rounded-md text-white font-medium ${
            loading || !passcode
              ? 'bg-blue-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? 'Loading...' : 'Fetch Deployments'}
        </button>
      </div>

      {error && (
        <p className="mt-4 text-red-600 font-medium">{error}</p>
      )}

      <div className="mt-6 flex gap-6">
        {/* Deployments List - Left Column */}
        {deployments.length > 0 && (
          <div className="w-1/3 bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Deployments</h2>
            </div>
            <div className="overflow-auto max-h-[calc(100vh-200px)]">
              {deployments.map((deployment) => (
                <div 
                  key={deployment.id}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                    selectedDeploymentId === deployment.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">
                      <span className="font-medium">Preview URL:</span>{' '}
                      <a href={deployment.url} target="_blank" rel="noopener noreferrer" 
                         className="text-blue-600 hover:underline">
                        {deployment.url}
                      </a>
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Branch:</span>{' '}
                      {deployment.deployment_trigger.metadata.branch}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Environment:</span>{' '}
                      {deployment.environment}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Status:</span>{' '}
                      <span className={
                        deployment.latest_stage.status === 'failure' 
                          ? 'text-red-600 font-semibold' 
                          : 'text-green-600'
                      }>
                        {deployment.latest_stage.status}
                      </span>
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Created:</span>{' '}
                      {new Date(deployment.created_on).toLocaleString()}
                    </p>
                    <button
                      onClick={() => fetchLogs(deployment.id)}
                      className="mt-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      View Logs
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Build Logs - Right Column */}
        {logs && logs.length > 0 && (
          <div className="flex-1 bg-white rounded-lg shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Build Logs</h2>
            </div>
            <div className="p-4">
              <pre className="bg-gray-50 p-4 rounded-md overflow-y-auto max-h-[calc(100vh-200px)] text-sm font-mono whitespace-pre-wrap break-words">
                {logs.map((log) => (
                  <div key={log.ts} className="py-1">
                    <span className="text-gray-500 inline-block">{new Date(log.ts).toLocaleString()}</span>
                    <span className="ml-2 text-gray-800 inline-block">{log.line}</span>
                  </div>
                ))}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
